import { ref, reactive, computed, watch, onBeforeUnmount } from '@vue/composition-api'
import store from '@/store'
import { useLambda } from '@/views/habit/awsLambda'
import { oid } from '@/helpers/mongoHelpers'
import { computeInherent, shouldRequireEipd } from '@/lib/privacy/calculations'
import i18n from '@/libs/i18n'
import useNotifications from '@/composables/useNotifications'

export default function useARIForm(props, { root, emit }) {
  const { getItemsAggregate } = useLambda()
  const { showErrorMessage } = useNotifications()
  const userData = store.state?.userStore?.userData
  const client_id = userData?.client?.$oid

  // Refs
  const ariFormRef = ref(null)
  const availableTreatments = ref([])
  const selectedTreatment = ref(null)
  const existingARIWarning = ref(false)
  const existingARIId = ref(null)
  const eipdDecisionMode = ref('auto')
  const isAutosaving = ref(false)
  const lastAutosaveTime = ref(null)
  let autosaveTimeout = null

  // Refs for validation observers
  const step1Tab = ref(null)
  const step2Tab = ref(null)
  const step3Tab = ref(null)
  const step4Tab = ref(null)
  const step5Tab = ref(null)
  const step6Tab = ref(null)

  // Tabs array for validation
  const tabs = [
    { name: 'step1', ref: step1Tab },
    { name: 'step2', ref: step2Tab },
    { name: 'step3', ref: step3Tab },
    { name: 'step4', ref: step4Tab },
    { name: 'step5', ref: step5Tab },
    { name: 'step6', ref: step6Tab },
  ]

  // Form data
  const formData = reactive({
    treatment_id: null,
    scores: {},
    legalObligations: {
      ol_a: false,
      ol_b: false,
      ol_c: false,
      ol_d: false,
    },
    eipdOverride: null,
    eipdOverrideJustification: '',
  })

  const calculatedResults = reactive({
    P: null,
    I: null,
    risk: null,
    level: '',
    requiresEipdAuto: false,
  })

  // Computed
  const eipdJustificationState = computed(() => {
    if (eipdDecisionMode.value !== 'manual') return null
    return formData.eipdOverrideJustification?.trim().length > 0 ? true : false
  })

  const canNavigateFromStep1 = computed(() => {
    return !existingARIWarning.value
  })

  // Watchers
  watch(eipdDecisionMode, newVal => {
    if (newVal === 'auto') {
      formData.eipdOverride = null
      formData.eipdOverrideJustification = ''
    }
  })

  // Auto-calculate results when scores or legal obligations change
  watch([() => formData.scores, () => formData.legalObligations, selectedTreatment], () => {
    if (selectedTreatment.value) {
      recalculateResults()

      // Debounce autosave to avoid creating multiple documents
      if (props.onSaveDraft) {
        // Clear previous timeout
        if (autosaveTimeout) {
          clearTimeout(autosaveTimeout)
        }

        // Set new timeout for autosave (wait 1 second after last change)
        // This also gives time for the async existingARI check to complete
        autosaveTimeout = setTimeout(() => {
          // Don't autosave if an ARI already exists for this treatment
          // Check inside timeout to ensure async handleTreatmentSelect completed
          if (existingARIWarning.value) {
            return
          }

          const draftPayload = {
            treatment_id: formData.treatment_id,
            scores: formData.scores,
            legalObligations: formData.legalObligations,
            calculatedP: calculatedResults.P,
            calculatedI: calculatedResults.I,
            inherentRisk: calculatedResults.risk,
            inherentLevel: calculatedResults.level || '',
            requiresEipdAuto: calculatedResults.requiresEipdAuto,
            eipdOverride: eipdDecisionMode.value === 'manual' ? formData.eipdOverride : null,
            eipdOverrideJustification: eipdDecisionMode.value === 'manual' ? formData.eipdOverrideJustification : '',
            eipdFinal: eipdDecisionMode.value === 'manual' ? formData.eipdOverride : calculatedResults.requiresEipdAuto,
          }

          isAutosaving.value = true
          props.onSaveDraft(draftPayload)
            .catch(error => {
              console.error('Error in autosave:', error)
            })
            .finally(() => {
              isAutosaving.value = false
              lastAutosaveTime.value = new Date()
            })
        }, 1000) // Wait 1 second after last change
      }
    }
  }, { deep: true })

  // Methods
  const fetchTreatments = async () => {
    try {
      const query = {
        client_id: oid(client_id),
        deleted: { $ne: true },
        status: 'active',
      }

      const pipeline = [
        { $match: query },
        {
          $project: {
            _id: 1,
            treatmentId: 1,
            nombre: 1,
            rol: 1,
            hasSensitive: 1,
            hasMinors: 1,
            hasBiometric: 1,
          }
        },
        { $sort: { treatmentId: 1 } }
      ]

      const result = await getItemsAggregate({ collection: 'treatment', pipeline })
      availableTreatments.value = result || []
    } catch (error) {
      console.error('Error fetching treatments:', error)
      root.$toast.error('Error al cargar tratamientos')
    }
  }

  const handleTreatmentSelect = async treatmentId => {
    if (!treatmentId) {
      selectedTreatment.value = null
      existingARIWarning.value = false
      existingARIId.value = null
      return
    }

    const treatment = availableTreatments.value.find(t => t._id === treatmentId)

    // If treatment not found, availableTreatments may not be loaded yet
    if (!treatment) {
      console.warn('Treatment not found in availableTreatments, may not be loaded yet')
      return
    }

    selectedTreatment.value = {
      ...treatment,
      rol: treatment.rol ? i18n.t(`label.${treatment.rol}`) : '',
    }

    // Check for existing ARI for this treatment
    try {
      const query = {
        client_id: oid(client_id),
        treatment_id: oid(treatmentId),
        deleted: { $ne: true },
      }

      const pipeline = [
        { $match: query },
        { $project: { _id: 1, ariId: 1 } },
        { $limit: 1 }
      ]

      const result = await getItemsAggregate({ collection: 'inherentAnalysis', pipeline })

      if (result && result.length > 0) {
        const foundAriId = result[0]._id

        // In edit mode, only warn if the found ARI is different from the current one being edited
        if (props.currentAriId && foundAriId === props.currentAriId) {
          // Same ARI being edited, no warning
          existingARIWarning.value = false
          existingARIId.value = null
        } else {
          // Different ARI exists, show warning
          existingARIWarning.value = true
          existingARIId.value = foundAriId
        }
      } else {
        existingARIWarning.value = false
        existingARIId.value = null
      }
    } catch (error) {
      console.error('Error checking existing ARI:', error)
    }
  }

  const recalculateResults = () => {
    const probabilityScores = probabilityFactors.value.map(f => {
      const score = formData.scores[f.id]
      return score === 'NA' ? null : score
    })

    const impactScores = impactFactors.value.map(f => {
      const score = formData.scores[f.id]
      return score === 'NA' ? null : score
    })

    const eipdScores = eipdFactors.value.map(f => formData.scores[f.id])

    const inherent = computeInherent({
      probability: probabilityScores,
      impact: impactScores,
    })

    const flags = {
      hasSensitive: selectedTreatment.value?.hasSensitive || false,
      hasMinors: selectedTreatment.value?.hasMinors || false,
      hasBiometric: selectedTreatment.value?.hasBiometric || false,
    }

    const requiresEipd = shouldRequireEipd(eipdScores, flags, formData.legalObligations)

    Object.assign(calculatedResults, {
      P: inherent.P,
      I: inherent.I,
      risk: inherent.risk,
      level: inherent.level || '',
      requiresEipdAuto: requiresEipd,
    })
  }

  // Validation function for a specific tab
  const validateTab = (tabRef) => new Promise((resolve, reject) => {
    tabRef.value.validate().then(success => {
      if (success) {
        resolve(true)
      } else {
        showErrorMessage(i18n.t('message.requiredFieldsIncomplete'))
        reject(new Error('Validation failed'))
      }
    })
  })

  // Validate all tabs before submit
  const validateAllTabs = async () => {
    const wizard = ariFormRef.value

    for (let i = 0; i < wizard.tabs.length; i++) {
      try {
        await validateTab(tabs[i].ref)
      } catch (error) {
        // If validation fails, set active tab to the failed one
        if (i !== wizard.activeTabIndex) {
          wizard.changeTab(wizard.activeTabIndex, i)
        }
        throw error
      }
    }
  }

  const handleSubmit = async () => {
    try {
      // Block navigation if an ARI already exists for this treatment
      if (existingARIWarning.value) {
        root.$toast.warning('Este tratamiento ya tiene un ARI. Use el botón "Editar existente" para modificarlo.')
        return
      }

      await validateAllTabs()
      // Results are already calculated automatically via watcher, no need to recalculate here

      if (eipdDecisionMode.value === 'manual' && !formData.eipdOverrideJustification?.trim()) {
        root.$toast.error('Debe proporcionar una justificación para el override manual')
        return
      }

      const payload = {
        treatment_id: formData.treatment_id,
        scores: formData.scores,
        legalObligations: formData.legalObligations,
        calculatedP: calculatedResults.P,
        calculatedI: calculatedResults.I,
        inherentRisk: calculatedResults.risk,
        inherentLevel: calculatedResults.level || '',
        requiresEipdAuto: calculatedResults.requiresEipdAuto,
        eipdOverride: eipdDecisionMode.value === 'manual' ? formData.eipdOverride : null,
        eipdOverrideJustification: eipdDecisionMode.value === 'manual' ? formData.eipdOverrideJustification : '',
        eipdFinal: eipdDecisionMode.value === 'manual' ? formData.eipdOverride : calculatedResults.requiresEipdAuto,
      }

      // Emit submit event with payload (handled by parent Add/Edit composable)
      emit('submit', payload)
    } catch (error) {
      // Validation failed, don't submit
    }
  }


  // Navigate to next tab with validation
  const goToNextTab = async () => {
    const index = ariFormRef.value.activeTabIndex

    // Block navigation if an ARI already exists for this treatment
    if (existingARIWarning.value) {
      root.$toast.warning('Este tratamiento ya tiene un ARI. Use el botón "Editar existente" para modificarlo.')
      return
    }

    try {
      await validateTab(tabs[index].ref)
      ariFormRef.value.nextTab()
    } catch (err) {
      // Validation error, don't advance
    }
  }

  const goToPreviousTab = () => {
    ariFormRef.value?.prevTab()
  }

  const formatAutosaveTime = timestamp => {
    if (!timestamp) return ''
    const date = new Date(timestamp)
    return date.toLocaleTimeString('es-CL', { hour: '2-digit', minute: '2-digit' })
  }

  // Cleanup timeout on component unmount
  onBeforeUnmount(() => {
    if (autosaveTimeout) {
      clearTimeout(autosaveTimeout)
    }
  })

  // Factor definitions
  const probabilityFactors = ref([
    {
      id: 'p_periodicidad',
      name: 'Periodicidad',
      rubric: {
        factor: 'Periodicidad',
        levels: [
          { score: 1, description: 'Puntual / único evento' },
          { score: 2, description: 'Esporádica (pocas veces al año)' },
          { score: 3, description: 'Mensual' },
          { score: 4, description: 'Semanal / diaria' },
          { score: 5, description: 'Continua / en tiempo real' },
        ],
      },
    },
    {
      id: 'p_medios_soporte',
      name: 'Medios soporte',
      rubric: {
        factor: 'Medios soporte',
        levels: [
          { score: 1, description: 'Digital cifrado + MFA + logs' },
          { score: 2, description: 'Digital con medidas estándar' },
          { score: 3, description: 'Digital no estructurado básico' },
          { score: 4, description: 'Híbrido papel + digital sin protocolo' },
          { score: 5, description: 'Papel sin custodia o digital sin controles' },
        ],
      },
    },
    {
      id: 'p_origen',
      name: 'Origen',
      rubric: {
        factor: 'Origen',
        levels: [
          { score: 1, description: 'Directamente del titular con consentimiento informado' },
          { score: 2, description: 'De un tercero responsable con legitimación documentada' },
          { score: 3, description: 'Fuente de acceso público verificada' },
          { score: 4, description: 'Generados por el responsable (inferidos, observados)' },
          { score: 5, description: 'Múltiple origen, con alguna fuente sin trazabilidad documentada' },
        ],
      },
    },
    {
      id: 'p_legitimacion',
      name: 'Legitimación',
      rubric: {
        factor: 'Legitimación',
        levels: [
          { score: 1, description: 'Consentimiento expreso, informado, inequívoco, con registro auditable' },
          { score: 2, description: 'Ejecución contractual clara / obligación legal expresa' },
          { score: 3, description: 'Interés legítimo con test de ponderación documentado' },
          { score: 4, description: 'Interés legítimo sin test formal / consentimiento tácito discutible' },
          { score: 5, description: 'Base legal débil, inexistente o no documentada' },
        ],
      },
    },
    {
      id: 'p_excepcion_sens',
      name: 'Excepción sens.',
      rubric: {
        factor: 'Excepción sens.',
        levels: [
          { score: 1, description: 'Excepción aplicable y documentada (consentimiento expreso, ley, etc.)' },
          { score: 2, description: 'Excepción aplicable con respaldo documental parcial' },
          { score: 3, description: 'Excepción discutible pero defendible' },
          { score: 4, description: 'Excepción no clara / no documentada' },
          { score: 5, description: 'Trata sensibles sin excepción válida identificable' },
          { score: 'NA', description: 'N/A — No se tratan datos sensibles' },
        ],
      },
    },
    {
      id: 'p_calidad_datos',
      name: 'Calidad datos',
      rubric: {
        factor: 'Calidad datos',
        levels: [
          { score: 1, description: 'Validación automática en origen + proceso documentado de actualización' },
          { score: 2, description: 'Autoreportados con validación parcial' },
          { score: 3, description: 'Autoreportados sin validación pero con posibilidad de actualización por titular' },
          { score: 4, description: 'Capturados una sola vez sin mecanismo de actualización' },
          { score: 5, description: 'Calidad desconocida o claramente desactualizados' },
        ],
      },
    },
    {
      id: 'p_info_transp',
      name: 'Info/transp.',
      rubric: {
        factor: 'Info/transp.',
        levels: [
          { score: 1, description: 'Aviso completo, accesible, lenguaje claro, actualizado, con registro de aceptación' },
          { score: 2, description: 'Aviso completo pero en lenguaje técnico o de acceso no evidente' },
          { score: 3, description: 'Aviso básico con elementos mínimos del Art. 14 Ley 21.719' },
          { score: 4, description: 'Aviso incompleto o desactualizado' },
          { score: 5, description: 'Sin aviso, o aviso incomprensible/oculto' },
        ],
      },
    },
    {
      id: 'p_conservacion',
      name: 'Conservación',
      rubric: {
        factor: 'Conservación',
        levels: [
          { score: 1, description: 'Plazo definido documentado + borrado automático al cumplirse' },
          { score: 2, description: 'Plazo definido, borrado manual periódico' },
          { score: 3, description: 'Plazo definido pero sin proceso activo de borrado' },
          { score: 4, description: 'Plazo no definido pero no indefinido' },
          { score: 5, description: 'Conservación indefinida sin justificación legal' },
        ],
      },
    },
    {
      id: 'p_soporte_cesion',
      name: 'Soporte cesión',
      rubric: {
        factor: 'Soporte cesión',
        levels: [
          { score: 1, description: 'Canal cifrado dedicado (API con autenticación mutua)' },
          { score: 2, description: 'Transferencia cifrada estándar (SFTP, HTTPS con autenticación)' },
          { score: 3, description: 'Email con adjunto cifrado' },
          { score: 4, description: 'Email con adjunto sin cifrar' },
          { score: 5, description: 'Canal no documentado o soporte físico sin custodia' },
          { score: 'NA', description: 'N/A — No hay cesiones' },
        ],
      },
    },
    {
      id: 'p_medidas_encargo',
      name: 'Medidas encargo',
      rubric: {
        factor: 'Medidas encargo',
        levels: [
          { score: 1, description: 'Contrato Art. 15 Ley 21.719 completo + auditoría anual + SLA de seguridad' },
          { score: 2, description: 'Contrato de encargo firmado con cláusulas estándar' },
          { score: 3, description: 'Contrato genérico sin cláusulas específicas de protección de datos' },
          { score: 4, description: 'Acuerdo informal o contrato sin cláusulas de encargo' },
          { score: 5, description: 'Sin contrato formal, relación de hecho' },
          { score: 'NA', description: 'N/A — No hay encargados' },
        ],
      },
    },
  ])

  const impactFactors = ref([
    {
      id: 'i_tipo_dato',
      name: 'Tipo de dato',
      rubric: {
        factor: 'Tipo de dato',
        levels: [
          { score: 1, description: 'Solo identificativos básicos' },
          { score: 2, description: 'Identificativos + contacto + comunes' },
          { score: 3, description: 'Económicos/financieros/crediticios' },
          { score: 4, description: 'Sensibles o NNA o biométricos no id.' },
          { score: 5, description: 'Sensibles alta potencialidad de daño' },
        ],
      },
    },
    {
      id: 'i_geolocalizacion',
      name: 'Geolocalización',
      rubric: {
        factor: 'Geolocalización',
        levels: [
          { score: 1, description: 'Ubicación aproximada puntual (comuna, región)' },
          { score: 2, description: 'Ubicación precisa pero puntual (dirección)' },
          { score: 3, description: 'Ubicación precisa con seguimiento esporádico' },
          { score: 4, description: 'Ubicación precisa con seguimiento frecuente (apps con geotracking)' },
          { score: 5, description: 'Ubicación precisa en tiempo real continuo (trazabilidad completa)' },
          { score: 'NA', description: 'N/A — No se tratan datos de geolocalización' },
        ],
      },
    },
    {
      id: 'i_volumen',
      name: 'Volumen',
      rubric: {
        factor: 'Volumen',
        levels: [
          { score: 1, description: 'Muy bajo (<100)' },
          { score: 2, description: 'Bajo (100–1.000)' },
          { score: 3, description: 'Medio (1.000–10.000)' },
          { score: 4, description: 'Alto (10.000–100.000)' },
          { score: 5, description: 'Muy alto / masivo (>100.000)' },
        ],
      },
    },
    {
      id: 'i_finalidad',
      name: 'Finalidad',
      rubric: {
        factor: 'Finalidad',
        levels: [
          { score: 1, description: 'Finalidad única, específica, explícita, evidentemente legítima' },
          { score: 2, description: 'Finalidades múltiples pero todas conexas y legítimas' },
          { score: 3, description: 'Finalidades múltiples con alguna conexión tenue entre ellas' },
          { score: 4, description: 'Finalidades amplias o compatibles agregadas posteriormente' },
          { score: 5, description: 'Finalidad vaga, genérica, o con usos compatibles no declarados inicialmente' },
        ],
      },
    },
    {
      id: 'i_proporcionalidad',
      name: 'Proporcionalidad',
      rubric: {
        factor: 'Proporcionalidad',
        levels: [
          { score: 1, description: 'Solo los datos estrictamente necesarios para la finalidad' },
          { score: 2, description: 'Datos necesarios + algún campo útil pero prescindible' },
          { score: 3, description: 'Algunos datos solicitados son excedentes respecto a la finalidad' },
          { score: 4, description: 'Varios campos recolectados sin justificación clara' },
          { score: 5, description: 'Recolección extensiva claramente excedente (fishing expedition)' },
        ],
      },
    },
    {
      id: 'i_cesiones',
      name: 'Cesiones',
      rubric: {
        factor: 'Cesiones',
        levels: [
          { score: 1, description: 'Cesión única a tercero conocido, con base legal clara' },
          { score: 2, description: '2–3 terceros por obligación legal' },
          { score: 3, description: 'Múltiples terceros variados con bases mixtas' },
          { score: 4, description: 'Cesiones frecuentes con legitimación debatible' },
          { score: 5, description: 'Cesiones extensas a terceros con finalidades propias' },
          { score: 'NA', description: 'N/A — No hay cesiones' },
        ],
      },
    },
    {
      id: 'i_encargados',
      name: 'Encargados',
      rubric: {
        factor: 'Encargados',
        levels: [
          { score: 1, description: 'Un solo encargado, contrato Art. 15 robusto, auditable' },
          { score: 2, description: '2–3 encargados, contratos estándar firmados' },
          { score: 3, description: 'Varios encargados, algunos sin auditoría reciente' },
          { score: 4, description: 'Múltiples encargados con subencargos declarados' },
          { score: 5, description: 'Cadena extensa de encargos/subencargos, control limitado' },
          { score: 'NA', description: 'N/A — No hay encargados' },
        ],
      },
    },
    {
      id: 'i_transferencias',
      name: 'Transferencias',
      rubric: {
        factor: 'Transferencias',
        levels: [
          { score: 1, description: 'Destino con nivel de protección reconocido por APDP' },
          { score: 2, description: 'Destino con cláusulas contractuales tipo vigentes' },
          { score: 3, description: 'Destino con mecanismo de transferencia documentado pero no preferente' },
          { score: 4, description: 'Destino sin nivel adecuado, con garantías parciales' },
          { score: 5, description: 'Destino sin garantías adecuadas ni mecanismo de transferencia válido' },
          { score: 'NA', description: 'N/A — No hay transferencias internacionales' },
        ],
      },
    },
  ])

  const eipdFactors = ref([
    {
      id: 'eipd_masivo',
      name: 'Masivo',
      rubric: {
        factor: 'Masivo',
        levels: [
          { score: 1, description: '<100 titulares' },
          { score: 2, description: '100–1.000' },
          { score: 3, description: '1.000–10.000' },
          { score: 4, description: '10.000–100.000' },
          { score: 5, description: '>100.000 o volumen calificable como gran escala bajo criterio APDP' },
        ],
      },
    },
    {
      id: 'eipd_monitoreo_publico',
      name: 'Monitoreo público',
      rubric: {
        factor: 'Monitoreo público',
        levels: [
          { score: 1, description: 'Sin monitoreo' },
          { score: 2, description: 'Cámara puntual sin grabación' },
          { score: 3, description: 'CCTV con grabación, zona acotada' },
          { score: 4, description: 'CCTV extenso con grabación prolongada' },
          { score: 5, description: 'Videovigilancia masiva + analítica (reconocimiento facial, conteo, tracking)' },
        ],
      },
    },
    {
      id: 'eipd_perfiles',
      name: 'Perfiles',
      rubric: {
        factor: 'Perfiles',
        levels: [
          { score: 1, description: 'Sin perfilamiento' },
          { score: 2, description: 'Segmentación simple por atributos declarados (edad, comuna)' },
          { score: 3, description: 'Segmentación con atributos derivados del comportamiento' },
          { score: 4, description: 'Perfilamiento predictivo con efectos en la experiencia del titular' },
          { score: 5, description: 'Perfilamiento con efectos jurídicos significativos o igualmente relevantes' },
        ],
      },
    },
    {
      id: 'eipd_dec_automatizadas',
      name: 'Dec. automatizadas',
      rubric: {
        factor: 'Dec. automatizadas',
        levels: [
          { score: 1, description: 'Sin decisiones automatizadas' },
          { score: 2, description: 'Automatización parcial con revisión humana obligatoria' },
          { score: 3, description: 'Decisión automatizada con posibilidad de apelación humana' },
          { score: 4, description: 'Decisión completamente automatizada sin intervención humana por defecto' },
          { score: 5, description: 'Decisión automatizada con efectos jurídicos significativos sobre el titular' },
        ],
      },
    },
    {
      id: 'eipd_tecnologias_invasivas',
      name: 'Tecnologías invasivas',
      rubric: {
        factor: 'Tecnologías invasivas',
        levels: [
          { score: 1, description: 'Tecnologías estándar, ampliamente adoptadas' },
          { score: 2, description: 'Tecnologías establecidas con nueva aplicación' },
          { score: 3, description: 'Tecnologías emergentes con algunos precedentes regulatorios' },
          { score: 4, description: 'Tecnologías novedosas sin precedente claro (IA generativa, biometría avanzada)' },
          { score: 5, description: 'Tecnologías invasivas (reconocimiento facial masivo, análisis emocional, neuromarketing)' },
        ],
      },
    },
  ])

  return {
    ariFormRef,
    availableTreatments,
    selectedTreatment,
    existingARIWarning,
    existingARIId,
    eipdDecisionMode,
    isAutosaving,
    lastAutosaveTime,
    formData,
    calculatedResults,
    probabilityFactors,
    impactFactors,
    eipdFactors,
    eipdJustificationState,
    canNavigateFromStep1,
    step1Tab,
    step2Tab,
    step3Tab,
    step4Tab,
    step5Tab,
    step6Tab,
    fetchTreatments,
    handleTreatmentSelect,
    recalculateResults,
    handleSubmit,
    goToNextTab,
    goToPreviousTab,
    formatAutosaveTime,
  }
}
